import { Component, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterOutlet } from "@angular/router";
import { SectionService } from '../../shared/services/section.service';
import { ComparablesService } from '../../shared/services/comparables.service';
import { Comparable } from '../../models/comparables.type';

@Component({
  selector: 'app-comparables',
  standalone: true,
  imports: [CommonModule, RouterOutlet],
  templateUrl: './comparables.component.html',
  styleUrl: './comparables.component.scss'
})
export class ComparablesComponent {
  loading = signal(true);
  error = signal<string | null>(null);
  activityId = signal<string>('');

  // keep a local copy for quick analytics
  comps = signal<Comparable[]>([]);

  // derived analytics
  total = computed(() => this.comps().length);
  salesCount = computed(() => this.comps().filter(c => (c.kind ?? c.snapshot?.kind) === 'Sales').length);
  rentCount = computed(() => this.comps().filter(c => (c.kind ?? c.snapshot?.kind) === 'Rent').length);
  expenseCount = computed(() => this.comps().filter(c => (c.kind ?? c.snapshot?.kind) === 'Expense').length);
  landCount = computed(() => this.comps().filter(c => (c.kind ?? c.snapshot?.kind) === 'Land').length);

  // a couple of lightweight analytics
  uniquePropertyTypes = computed(() => {
    const set = new Set(this.comps().map(c => c.snapshot?.propertyType ?? '').filter(Boolean));
    return set.size;
  });
  topCity = computed(() => {
    const counts = new Map<string, number>();
    for (const c of this.comps()) {
      const city = c.snapshot?.city?.trim() || '';
      if (!city) continue;
      counts.set(city, (counts.get(city) ?? 0) + 1);
    }
    let top = '';
    let max = 0;
    counts.forEach((v, k) => { if (v > max) { max = v; top = k; } });
    return top || '—';
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private comparablesSvc: ComparablesService,
    private sectionService: SectionService
  ) {}

  ngOnInit(): void {
    this.sectionService.setSelectedSubSection('Comparables');
    this.loading.set(true);
    this.error.set(null);

    // read :id and load
    const id = this.route.snapshot.paramMap.get('id') ?? '';
    this.activityId.set(id);

    this.comparablesSvc.loadComparables(id).subscribe({
      next: list => {
        this.comps.set(list ?? []);
        this.loading.set(false);
      },
      error: err => {
        this.error.set('Failed to load comparables.');
        console.error(err);
        this.loading.set(false);
      },
    });
  }

  goTo(kind: 'sales' | 'rent' | 'expense' | 'land') {
    this.router.navigate([kind], { relativeTo: this.route });
  }
}
